import type { Tool } from '@google/genai'
export type {
  CompanyContext,
  PersonContext,
  StrategicContext,
  ResearchResult,
} from '../core/intelligence/lead-research.js'

export enum LiveConnectionState {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  ERROR = 'ERROR',
}

export interface AudioConfig {
  sampleRate: number
  channels: number
}

export interface GroundingChunk {
  web?: {
    uri: string
    title: string
  }
  maps?: {
    uri: string
    title: string
  }
  thoughtSignature?: string
}

export interface GroundingMetadata {
  groundingChunks: GroundingChunk[]
  /**
   * Claim-level grounding supports from Gemini.
   * Each support links a text segment (by character indices) to one or more grounding chunks.
   */
  groundingSupports?: Array<{
    segment?: { startIndex?: number; endIndex?: number }
    groundingChunkIndices?: number[]
  }>
  webSearchQueries?: string[]
  searchEntryPoint?: { renderedContent?: string; sdkBlob?: string } | string
}

export interface ChainOfThoughtStep {
  label: string
  description?: string
  status: 'complete' | 'active' | 'pending'
  timestamp?: number
}

export interface TranscriptItem {
  id: string
  role: 'user' | 'model'
  text: string
  timestamp: Date
  isFinal: boolean
  reasoning?: string // For Chain of Thought UI
  chainOfThought?: { steps: ChainOfThoughtStep[] } // Safe, high-level reasoning steps
  tools?: Array<{
    name: string
    type?: string
    state?: 'running' | 'complete' | 'error'
    input?: unknown
    output?: unknown
    error?: string
    startedAt?: number
    finishedAt?: number
  }> // Per-message tool timeline
  status?: 'streaming' | 'complete' | 'error' // For streaming UI and error states
  isLiveTranscript?: boolean // True for voice-mode streaming transcript items
  turnId?: string // Canonical Live turn identity for transcript reconciliation
  processingTime?: number // Response time in milliseconds
  error?: {
    type: 'network' | 'rate_limit' | 'auth' | 'quota' | 'timeout' | 'server' | 'unknown'
    message: string
    details?: string
    retryable?: boolean
  }
  contextSources?: Array<{
    type: 'company' | 'person' | 'location' | 'conversation' | 'file' | 'webcam' | 'screen' | 'web'
    label: string
    value?: string
    url?: string
  }>
  attachment?: {
    type:
      | 'image'
      | 'file'
      | 'research-card'
      | 'calendar_widget'
      | 'discovery_report'
      | 'proposal'
      | 'intelligence_briefing' // Added intelligence_briefing
      | 'visual_observation'
    url?: string // base64 data url or blob url for UI, or booking URL for calendar_widget
    mimeType?: string
    data?: string // Raw base64 string for API or JSON string for research-card/calendar_widget/discovery_report
    name?: string
    htmlContent?: string // HTML content for discovery_report inline preview
  }
  groundingMetadata?: GroundingMetadata | undefined
}

export type VisualShape =
  | 'orb'
  | 'wave'
  | 'dna'
  | 'rect'
  | 'planet'
  | 'heart'
  | 'grid'
  | 'atom'
  | 'hourglass'
  | 'shield'
  | 'star'
  | 'globe'
  | 'brain'
  | 'constellation'
  | 'weather'
  | 'chart'
  | 'map'
  | 'clock'
  | 'code'
  | 'text'
  | 'scanner'
  | 'vortex'
  | 'fireworks'
  | 'lightning'
  | 'flower'
  | 'discovery'
  | 'scoring'
  | 'workshop'
  | 'consulting'
  | 'closer'
  | 'summary'
  | 'proposal'
  | 'admin'
  | 'retargeting'
  | 'oscilloscope'
  | 'spectrum'
  | 'toolCall'
  | 'functionPulse'
  | 'solarSystem'
  | 'network'
  | 'molecule'
  | 'mathGraph'
  | 'neural_network'
  | 'error'
  | 'screenshare'
  | 'webcam'

export type VisualCanvasAnchor = 'agent_center' | 'chat_center' | 'between_centers'

export interface VisualState {
  isActive: boolean
  audioLevel: number // 0.0 to 1.0
  mode: 'listening' | 'thinking' | 'speaking' | 'idle'
  shape: VisualShape
  textContent?: string // Dynamic text to render
  weatherData?: {
    condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'stormy' | 'windy' | 'fog'
    temperature?: string
    temperatureValue?: number // Numeric value for particle rendering (Celsius)
    location?: string
  }
  chartData?: {
    trend: 'up' | 'down' | 'neutral'
    value?: string
    numericValue?: number // Numeric value for particle rendering
    label?: string
  }
  stockData?: {
    symbol: string
    price: number
    change: number
    changePercent?: number
  }
  mapData?: {
    title: string
    lat?: number
    lng?: number
    destination?: {
      title: string
      lat: number
      lng: number
    }
  }
  // New context-driven data properties
  solarSystemData?: {
    focusPlanet?: string
    showOrbits?: boolean
    scale?: 'realistic' | 'educational'
  }
  networkData?: {
    nodes: string[]
    connections: Array<{ from: string; to: string; strength: number }>
    layout: 'force' | 'circular' | 'hierarchical'
  }
  moleculeData?: {
    formula?: string
    structure?: 'ball-stick' | 'space-filling' | 'wireframe'
    animate?: boolean
  }
  mathData?: {
    equation?: string
    graph3d?: boolean
    domain?: [number, number]
    range?: [number, number]
  }
  // Metadata visualizations
  citationCount?: number // Number of citations/sources
  reasoningComplexity?: number // 0.0 to 1.0 based on reasoning length/complexity
  researchActive?: boolean // Whether research queries are active
  sourceCount?: number // Number of verified sources

  // New visual enhancement properties
  // Trails & Effects
  trailsEnabled?: boolean
  bloomEnabled?: boolean
  bloomIntensity?: number // 0.0-2.0
  bloomRadius?: number // 4-16px

  // Audio visualization
  waveformEnabled?: boolean
  waveformMode?: 'oscilloscope' | 'spectrum'

  // Gesture & Interaction

  // Morphing
  morphingTo?: VisualShape | null
  morphProgress?: number // 0.0-1.0

  // Tool calling
  toolCallData?: {
    name: string
    progress: number // 0.0-1.0
    state: 'running' | 'complete' | 'error'
  }

  // Research activity
  researchData?: {
    activeQueries: number
    intensity: number // 0.0-1.0
    insightFound?: number // timestamp
  }

  // Neural network research visualization
  researchState?: {
    nodes: Array<{
      x: number // 0-1 normalized position
      y: number // 0-1 normalized position
      status: 'pending' | 'active' | 'completed'
    }>
  }

  // Theme overrides
  theme?: string
  particleColors?: {
    primary: string
    secondary: string
    accent: string
  }

  /** Dual-anchor canvas positioning: which center the visual orbits around */
  anchor?: VisualCanvasAnchor
  anchorBias?: number
  anchorLabel?: string
  anchorReason?: string

  /** Structured vision HUD (ASCII strip) — from latest visualObservations / vision analysis */
  visionHud?: {
    modality: 'webcam' | 'screen' | 'upload'
    live?: boolean
    ascii: string
    confidence?: number
  }
}

export interface LiveServiceConfig {
  apiKey?: string
  modelId?: string // Legacy client-side field; server owns Live model selection
  model?: string // Optional alias
  voiceName?: string // Voice selection
  languageCode?: string
  responseMode?: 'respond_immediately' | 'listen_only' | 'respond_on_direct_address' | 'respond_on_visual_trigger'
  affectiveState?: {
    affect: 'neutral' | 'positive' | 'frustrated' | 'stressed' | 'excited'
    energy: 'low' | 'medium' | 'high'
    preferredPace: 'slow' | 'normal' | 'fast'
    preferredVerbosity: 'terse' | 'balanced' | 'detailed'
    confidence?: number
    source?: 'default' | 'text' | 'voice' | 'manual' | 'system'
    updatedAt?: string
  }
  visualAttentionEnabled?: boolean
  proactiveAudioEnabled?: boolean
  sessionId?: string // Added for Voice session sync
  tools?: Tool[] // Legacy client-side field; server owns Live tools
  systemInstruction?: string // Legacy client-side field; server owns Live prompt
  onStateChange: (state: string) => void
  onTranscript: (
    text: string,
    isUser: boolean,
    isFinal: boolean,
    groundingMetadata?: GroundingMetadata,
    agentMetadata?: { agent?: string; stage?: string }
  ) => void
  onVolumeChange: (inputVol: number, outputVol: number) => void
  onToolCall?: (
    functionCalls: Array<{ id: string; name: string; args: Record<string, unknown> }>
  ) => Promise<Array<Record<string, unknown>>> // Added Handler
  onSignal?: (payload: { action: string; data?: Record<string, unknown> }) => void
}

// --- RESEARCH & CONTEXT TYPES ---
// CompanyContext, PersonContext, StrategicContext, ResearchResult are canonical in
// src/core/intelligence/lead-research.ts and re-exported at the top of this file.

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
}

export interface GeminiSession {
  sendRealtimeInput: (input: Record<string, unknown>) => Promise<void>
  sendToolResponse: (response: Record<string, unknown>) => Promise<void>
  close: () => Promise<void>
}
